import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MenuItem, ItemOption, MenuItemOptionValue } from "@/redux/services/menu/menu.type";
import { cn, displayPrice, getImageUrl, getMenuItemDisplayPrice } from "@/lib/utils";
import { Check, Minus, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

interface ItemOptionSheetProps {
  open: boolean;
  onClose: () => void;
  item: MenuItem | null;
  currency: string;
  onAddToCart: (
    item: MenuItem,
    quantity: number,
    selectedOptionValueIds: string[],
    additionalDemands: string
  ) => void;
}

const ItemOptionSheet: React.FC<ItemOptionSheetProps> = ({
  open,
  onClose,
  item,
  currency,
  onAddToCart,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedValues, setSelectedValues] = useState<Record<string, string[]>>(
    {}
  );
  const [additionalDemands, setAdditionalDemands] = useState("");

  const resetState = useCallback(() => {
    setQuantity(1);
    setSelectedValues({});
    setAdditionalDemands("");
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetState();
      onClose();
    }
  };

  const toggleValue = (optionId: string, valueId: string, type: string, maxSelections: number) => {
    setSelectedValues((prev) => {
      const current = prev[optionId] ?? [];

      // Radio & select: single selection, clicking same deselects
      if (type === "radio" || type === "select") {
        if (current.includes(valueId)) {
          return { ...prev, [optionId]: [] };
        }
        return { ...prev, [optionId]: [valueId] };
      }

      // Checkbox: multi selection
      if (current.includes(valueId)) {
        return { ...prev, [optionId]: current.filter((id) => id !== valueId) };
      }
      if (maxSelections > 0 && current.length >= maxSelections) {
        return prev;
      }
      return { ...prev, [optionId]: [...current, valueId] };
    });
  };

  const selectValue = (optionId: string, valueId: string) => {
    setSelectedValues((prev) => ({
      ...prev,
      [optionId]: valueId ? [valueId] : [],
    }));
  };

  // Get price modifier for a value, considering templates and dependent options
  const getValuePrice = (itemOption: ItemOption, valueId: string): number => {
    // 1. Check itemOptionPrices (direct price modifier)
    const optionPrice = itemOption.itemOptionPrices.find(
      (p) => p.menuItemOptionValue.id === valueId
    );
    if (optionPrice) {
      return optionPrice.priceModifier ?? 0;
    }

    const value = itemOption.menuItemOption.menuItemOptionValues.find(
      (v: MenuItemOptionValue) => v.id === valueId
    );
    if (!value) return 0;

    // 2. Template options: use value's basePrice
    if (itemOption.menuItemOption.isTemplate) {
      return value.basePrice ?? 0;
    }

    // 3. Dependent options: look up price based on parent option's selected value
    if (itemOption.menuItemOption.dependsOnOption) {
      const parentOptionId = itemOption.menuItemOption.dependsOnOption.id;
      // Find which itemOption corresponds to the parent
      const parentItemOption = item?.itemOptions.find(
        (opt) => opt.menuItemOption.id === parentOptionId
      );
      if (parentItemOption) {
        const parentSelectedIds = selectedValues[parentItemOption.id] ?? [];
        if (parentSelectedIds.length > 0) {
          const dependentPrice = value.valueDependentPrices?.find(
            (dp) => dp.dependsOnValue?.id === parentSelectedIds[0]
          );
          if (dependentPrice) {
            return dependentPrice.priceModifier ?? 0;
          }
        }
      }
    }

    return 0;
  };

  // Display price for an option value (used in the UI)
  const getDisplayPriceModifier = (itemOption: ItemOption, valueId: string): number => {
    return getValuePrice(itemOption, valueId);
  };

  const optionsTotal = useMemo(() => {
    if (!item) return 0;
    let total = 0;
    for (const itemOption of item.itemOptions) {
      const selected = selectedValues[itemOption.id] ?? [];
      for (const valueId of selected) {
        total += getValuePrice(itemOption, valueId);
      }
    }
    return total;
  }, [item, selectedValues]);

  const baseDisplayPrice = item ? getMenuItemDisplayPrice(item) : 0;
  const unitTotal = (item?.basePrice ?? 0) + optionsTotal;
  const lineTotal = unitTotal * quantity;

  const requiredOptionsMet = useMemo(() => {
    if (!item) return false;
    return item.itemOptions
      .filter((opt) => opt.isRequired)
      .every((opt) => (selectedValues[opt.id]?.length ?? 0) > 0);
  }, [item, selectedValues]);

  const handleAdd = () => {
    if (!item || !requiredOptionsMet) return;
    const allSelectedIds = Object.values(selectedValues).flat();
    onAddToCart(item, quantity, allSelectedIds, additionalDemands.trim());
    resetState();
    onClose();
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex flex-col p-0 gap-0">
        <DialogHeader className="p-5 pb-0">
          <div className="flex items-center gap-4">
            <img
              src={getImageUrl(item.image?.url)}
              alt={item.title}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            />
            <div className="min-w-0">
              <DialogTitle className="text-base truncate">
                {item.title}
              </DialogTitle>
              <DialogDescription className="text-sm font-semibold text-primary mt-0.5">
                {displayPrice(baseDisplayPrice, currency)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Options list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {item.itemOptions.map((itemOption) => {
            const option = itemOption.menuItemOption;
            const values = option.menuItemOptionValues ?? [];
            const selected = selectedValues[itemOption.id] ?? [];

            return (
              <div key={itemOption.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold">
                    {option.title}
                  </h4>
                  {itemOption.isRequired && (
                    <span className="text-[10px] font-semibold bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
                      Obligatoire
                    </span>
                  )}
                  {itemOption.maxSelections > 1 && (
                    <span className="text-[10px] text-muted-foreground">
                      (max {itemOption.maxSelections})
                    </span>
                  )}
                </div>

                {option.type === "select" ? (
                  <Select
                    value={selected[0] ?? ""}
                    onValueChange={(val) => selectValue(itemOption.id, val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir une option" />
                    </SelectTrigger>
                    <SelectContent>
                      {values.map((value) => {
                        const priceModifier = getDisplayPriceModifier(itemOption, value.id);
                        return (
                          <SelectItem key={value.id} value={value.id}>
                            {value.title}
                            {priceModifier > 0 &&
                              ` (+${displayPrice(priceModifier, currency)})`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="space-y-1">
                    {values.map((value) => {
                      const isSelected = selected.includes(value.id);
                      const priceModifier = getDisplayPriceModifier(itemOption, value.id);
                      const isRadio = option.type === "radio";

                      return (
                        <button
                          key={value.id}
                          onClick={() =>
                            toggleValue(
                              itemOption.id,
                              value.id,
                              option.type,
                              itemOption.maxSelections
                            )
                          }
                          className={cn(
                            "w-full flex items-center justify-between p-3 rounded-lg border text-sm transition-all",
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/30"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={cn(
                                "w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                                isRadio ? "rounded-full" : "rounded",
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-border"
                              )}
                            >
                              {isSelected && (
                                <Check size={12} className="text-white" />
                              )}
                            </div>
                            <span className={isSelected ? "font-medium" : ""}>
                              {value.title}
                            </span>
                          </div>
                          {priceModifier > 0 && (
                            <span className="text-xs text-muted-foreground">
                              +{displayPrice(priceModifier, currency)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Additional demands */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Notes</h4>
            <textarea
              value={additionalDemands}
              onChange={(e) => setAdditionalDemands(e.target.value)}
              placeholder="Instructions spéciales..."
              rows={2}
              className="w-full rounded-lg border border-border p-3 text-sm placeholder:text-muted-foreground outline-none focus:border-primary transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border space-y-3">
          {/* Quantity */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Quantité</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
              >
                <Minus size={16} />
              </button>
              <span className="text-sm font-semibold w-6 text-center">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <Button
            onClick={handleAdd}
            disabled={!requiredOptionsMet}
            className="w-full"
            size="lg"
          >
            Ajouter — {displayPrice(lineTotal, currency)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ItemOptionSheet;
